import { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { exchangeGoogleHandoffAction } from '../../stores/actions/authActions';
import { closeModal, showError } from '../../stores/slices/commonSlice';
import {
    GOOGLE_REDIRECT_HANDOFF_KEY,
    GOOGLE_REDIRECT_ERROR_KEY,
    GOOGLE_REDIRECT_ERROR_MESSAGES,
} from '../../config/oauth';

/**
 * Landing side of the redirect "Continue with Google" flow.
 *
 * /oauth/callback on the apex sends the browser back to the page the visitor
 * started on with the outcome in the URL FRAGMENT:
 *
 *     https://foammadnessja.apsession.com/#gauth=<handoff>
 *     https://foammadnessja.apsession.com/#gauth_error=cancelled
 *
 * The fragment never reaches a server, so the handoff stays out of access
 * logs and Referer headers. This component runs once on boot, redeems the
 * handoff for a token through the normal auth store, and scrubs the fragment
 * from the address bar so a reload or a shared link cannot replay it (the
 * backend rejects a second redemption anyway).
 */
function readOutcome() {
    const hash = window.location.hash || '';
    if (hash.length < 2) return null;
    const params = new URLSearchParams(hash.slice(1));
    const handoff = params.get(GOOGLE_REDIRECT_HANDOFF_KEY);
    const error = params.get(GOOGLE_REDIRECT_ERROR_KEY);
    if (!handoff && !error) return null;
    return { handoff, error };
}

function scrubFragment() {
    try {
        const clean = window.location.pathname + window.location.search;
        window.history.replaceState(window.history.state, '', clean);
    } catch {
        // Nothing to do — the backend rejects a replayed handoff regardless.
    }
}

export default function GoogleRedirectReturn() {
    const dispatch = useDispatch();

    useEffect(() => {
        const outcome = readOutcome();
        if (!outcome) return;
        scrubFragment();

        if (outcome.handoff) {
            dispatch(exchangeGoogleHandoffAction({ handoff: outcome.handoff, platform: 'web' })).then((res) => {
                if (res.meta.requestStatus === 'fulfilled') {
                    dispatch(closeModal());
                }
            });
            return;
        }

        const message = GOOGLE_REDIRECT_ERROR_MESSAGES[outcome.error] ?? GOOGLE_REDIRECT_ERROR_MESSAGES.failed;
        if (message) {
            dispatch(showError(message));
        }
    }, [dispatch]);

    return null;
}
